import * as React from 'react';

import {
  DialogContent,
  DialogFooter,
  DialogTrigger,
  DialogOverlay,
  cn,
} from '../..';

import { DialogClosingConfirmation } from './DialogClosingConfirmation';

export type ConsoleDialogProps = {
  title: React.ReactNode;
  titleClassName?: string;
  isOpen: boolean;
  size?: 'fill' | 'wrap';
  onOpenChange: (isOpen: boolean) => void;
  content: React.ReactNode;
  contentClassName?: string;
  dialogClassName?: string;
  footer?: React.ReactNode;
  className?: string;
  isLoading?: boolean;
  askBeforeClosing?:
    | boolean
    | {
        shouldAsk: boolean;
        message: string;
      };
  closeWithEscapeKey?: boolean;
  closeOnClickOutside?: boolean;
};

export const ConsoleDialog = ({
  isOpen,
  size = 'wrap',
  onOpenChange,
  title,
  titleClassName,
  content,
  contentClassName,
  dialogClassName,
  footer,
  className,
  isLoading,
  askBeforeClosing,
  closeWithEscapeKey = true,
  closeOnClickOutside = true,
}: ConsoleDialogProps) => {
  const dialogOverlayRef = React.useRef<HTMLDivElement | null>(null);

  const shouldAskBeforeClosing = React.useMemo(() => {
    if (typeof askBeforeClosing === 'boolean') {
      return askBeforeClosing;
    }
    return askBeforeClosing?.shouldAsk ?? false;
  }, [askBeforeClosing]);

  const askBeforeClosingMessage = React.useMemo(() => {
    if (typeof askBeforeClosing === 'boolean') {
      return 'Do you want to discard unsaved changes?';
    }
    return (
      askBeforeClosing?.message ?? 'Do you want to discard unsaved changes?'
    );
  }, [askBeforeClosing]);

  const [showClosingConfirmation, setShowClosingConfirmation] =
    React.useState(false);

  const handleOverlayPointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const didClickOverlay = e.target === dialogOverlayRef.current;

      if (!didClickOverlay || !closeOnClickOutside) return;

      if (shouldAskBeforeClosing) {
        setShowClosingConfirmation(true);
      } else {
        onOpenChange(false);
      }
    },
    [closeOnClickOutside, onOpenChange, shouldAskBeforeClosing],
  );

  const shouldAskBeforeClosingRef = React.useRef(shouldAskBeforeClosing);
  shouldAskBeforeClosingRef.current = shouldAskBeforeClosing;

  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeWithEscapeKey) {
        if (shouldAskBeforeClosingRef.current) {
          setShowClosingConfirmation(true);
        } else {
          onOpenChange(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onOpenChange, closeWithEscapeKey]);

  return (
    <DialogTrigger isOpen={isOpen}>
      <DialogOverlay
        ref={dialogOverlayRef}
        className={cn('@ConsoleDialog', className)}
        isDismissable={false}
        isKeyboardDismissDisabled={true}
        onPointerDownCapture={handleOverlayPointerDown}
      >
        <DialogContent
          className={'relative overflow-clip'}
          dialogClassName={dialogClassName}
          position="center"
          isLoading={isLoading}
          onOpenChange={onOpenChange}
          size={size}
          onBeforeClose={() => {
            if (shouldAskBeforeClosing) {
              setShowClosingConfirmation(true);
              return { shouldClose: false };
            }
            return { shouldClose: true };
          }}
        >
          <div
            className={cn(
              'flex flex-col space-y-1.5 border-b border-neutral/10 p-4 text-center text-lg font-semibold sm:text-left',
              titleClassName,
            )}
          >
            {title}
          </div>
          <div
            className={cn(
              'no-scrollbar h-full min-w-[600px] overflow-y-auto px-5 pt-4 pb-5',
              contentClassName,
            )}
            style={{ maxHeight: size === 'fill' ? undefined : '70vh' }}
          >
            {content}
          </div>
          {footer && <DialogFooter>{footer}</DialogFooter>}
          <DialogClosingConfirmation
            isOpen={showClosingConfirmation}
            message={askBeforeClosingMessage}
            onCancel={() => setShowClosingConfirmation(false)}
            onDiscard={() => {
              setShowClosingConfirmation(false);
              onOpenChange(false);
            }}
          />
        </DialogContent>
      </DialogOverlay>
    </DialogTrigger>
  );
};
